/**
 * Frontend Tool Registry for In-App Agent
 *
 * This registry manages FE tools that can be executed locally in the browser.
 * Tools are registered by apps and can be called by the AI agent to manipulate UI state.
 *
 * Tool scopes:
 * - "global" - Available everywhere (fe_show / fe_close navigation, Ask Ani)
 * - "cash-flow" - Cash Flow app specific tools
 * - "screener" - Screener app specific tools
 * - etc.
 */

import { closeAniRef, showAniRef, type AniRefHandlers } from "@/services/agent/aniRef";
import { executeAskAniHandoff } from "@/services/inAppAgent/reviewSurfaceAgentBridge";

export interface FeToolResult {
  success: boolean;
  message?: string;
  data?: Record<string, unknown>;
}

export interface FeToolParameter {
  name: string;
  type: "string" | "number" | "boolean" | "object" | "array";
  required: boolean;
  description: string;
  enum?: string[];
}

export interface FeTool {
  id: string;
  scope: "global" | string;
  displayName: string;
  description: string;
  parameters: FeToolParameter[];
  execute: (params: Record<string, unknown>) => Promise<FeToolResult>;
}

export interface FeToolSchema {
  id: string;
  scope: string;
  displayName: string;
  description: string;
  parameters: FeToolParameter[];
}

class FeToolRegistry {
  private tools: Map<string, FeTool> = new Map();

  /**
   * Register a new FE tool
   */
  register(tool: FeTool): void {
    if (this.tools.has(tool.id)) {
      console.warn(`[FeToolRegistry] Tool ${tool.id} already registered, overwriting`);
    }
    this.tools.set(tool.id, tool);
  }

  /**
   * Unregister a tool (useful for cleanup when app unmounts)
   */
  unregister(toolId: string): void {
    this.tools.delete(toolId);
  }

  /**
   * Get all tools available for a specific app
   * Returns global tools + app-specific tools
   */
  getToolsForApp(appId: string): FeTool[] {
    return Array.from(this.tools.values()).filter(
      (t) => t.scope === "global" || t.scope === appId
    );
  }

  /**
   * Get tool schemas for a specific app (for sending to backend/LLM)
   * This is a lightweight version without the execute function
   */
  getToolSchemasForApp(appId: string): FeToolSchema[] {
    return this.getToolsForApp(appId).map((tool) => ({
      id: tool.id,
      scope: tool.scope,
      displayName: tool.displayName,
      description: tool.description,
      parameters: tool.parameters,
    }));
  }

  /**
   * Get a specific tool by ID
   */
  getTool(toolId: string): FeTool | undefined {
    return this.tools.get(toolId);
  }

  /**
   * Check if a tool exists
   */
  hasTool(toolId: string): boolean {
    return this.tools.has(toolId);
  }

  /**
   * Execute a tool by ID with given parameters
   */
  async execute(toolId: string, params: Record<string, unknown>): Promise<FeToolResult> {
    const tool = this.tools.get(toolId);
    if (!tool) {
      return {
        success: false,
        message: `Unknown FE tool: ${toolId}`,
      };
    }

    if (runtimeFeToolAllowlist !== undefined && runtimeFeToolAllowlist !== null) {
      if (!runtimeFeToolAllowlist.includes(toolId)) {
        return {
          success: false,
          message: `Tool ${toolId} is not allowed for this profile`,
        };
      }
    }

    try {
      return await tool.execute(params);
    } catch (error) {
      console.error(`[FeToolRegistry] Error executing tool ${toolId}:`, error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Unknown error executing tool",
      };
    }
  }

  /**
   * Get all registered tool IDs (for debugging)
   */
  getAllToolIds(): string[] {
    return Array.from(this.tools.keys());
  }

  /**
   * Clear all registered tools (useful for testing)
   */
  clear(): void {
    this.tools.clear();
  }
}

export const feToolRegistry = new FeToolRegistry();

export const OPENWORK_DEMO_OPEN_APP_IDS = ["home", "task-review", "artifacts", "artifact"] as const;

let runtimeAllowedOpenAppIds: readonly string[] | undefined;
let runtimeFeToolAllowlist: readonly string[] | undefined | null;
let _runtimePriorityTaskId: string | undefined;

/** Called when in-app agent profile loads (OpenWork demo). */
export function setFeOpenAppAllowlist(appIds: readonly string[] | undefined): void {
  runtimeAllowedOpenAppIds = appIds;
}

export function getFeOpenAppAllowlist(): readonly string[] | undefined {
  return runtimeAllowedOpenAppIds;
}

/** When set, only listed FE tools may execute. null = unrestricted; [] = deny all. */
export function setFeToolAllowlist(toolIds: readonly string[] | undefined | null): void {
  runtimeFeToolAllowlist = toolIds;
}

/** Primary letter-review task id from home standup context. */
export function setInAppAgentPriorityTaskId(taskId: string | undefined): void {
  _runtimePriorityTaskId = taskId;
}

const REF_DESCRIPTION =
  "What to show: kind:id (portfolio:port-1, meeting:abc, artifact:<id>), app:<app_id>, app:<app_id>/kind:id (highlight a list row), " +
  "review:<task_id>, a window ref followed by /tab/section (portfolio:port-1/holdings), a path relative to the focused window " +
  "(holdings), or global_search:<query>. Copy refs from os_context windows, nav_targets and tool results.";

/** Register global FE tools (navigation + Ask Ani). Called once from OsLayout. */
export function registerGlobalTools(handlers: AniRefHandlers): void {
  const show = (ref: string) => showAniRef(ref, handlers);

  feToolRegistry.register({
    id: "fe_show",
    scope: "global",
    displayName: "Show",
    description: "Open or focus a window, switch tab, highlight a section or list row, or open global search",
    parameters: [{ name: "ref", type: "string", required: true, description: REF_DESCRIPTION }],
    execute: async (params) => show(String(params.ref ?? "")),
  });

  feToolRegistry.register({
    id: "fe_close",
    scope: "global",
    displayName: "Close",
    description: "Close a window — the focused one when ref is omitted",
    parameters: [{ name: "ref", type: "string", required: false, description: "Window ref from os_context (e.g. portfolio:port-1, app:meetings)" }],
    execute: async (params) => closeAniRef(typeof params.ref === "string" ? params.ref : undefined, handlers),
  });

  feToolRegistry.register({
    id: "fe_open_ask_ani",
    scope: "global",
    displayName: "Open Ask Ani",
    description: "Open the Ask Ani drawer on a letter for edits and proofreading, optionally with a draft message",
    parameters: [
      { name: "ref", type: "string", required: false, description: "Letter to open first: artifact:<id> or review:<task_id>. Omit to use the open letter" },
      { name: "draft", type: "string", required: false, description: "Message to pre-fill in the Ask Ani composer" },
      { name: "scope_text", type: "string", required: false, description: "Quoted letter text to scope the Ask Ani turn" },
    ],
    execute: (params) => executeAskAniHandoff(params, show),
  });
}
